'use client';

import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Settings } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useConsent } from '@/lib/consent/consent-context';
import type { ConsentCategory } from '@/lib/consent/types';
import Link from 'next/link';

// ─── Category Config ──────────────────────────────────────────────────────────

type CategoryConfig = {
  key: keyof ConsentCategory;
  label: string;
  description: string;
  required: boolean;
};

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'necessary',
    label: 'Strictly Necessary',
    description:
      'Required for the site to function. These cannot be disabled. They include session handling and security cookies.',
    required: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description:
      'Helps us understand how visitors interact with our site using Google Analytics. All data is aggregated and anonymous.',
    required: false,
  },
];

type View = 'banner' | 'preferences';

export function ConsentBanner() {
  const { state, acceptAll, declineAll, saveCustom } = useConsent();
  const [view, setView] = useState<View>('banner');
  const [categories, setCategories] = useState<ConsentCategory>({
    necessary: true,
    analytics: false,
  });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't render anything until client has resolved real consent state
  // This prevents the banner flashing on returning visitors on iOS
  if (!mounted) return null;

  // Drawer is open whenever the user hasn't decided yet
  const isOpen = state.status === 'undecided';

  function handleToggle(key: keyof ConsentCategory, value: boolean) {
    setCategories((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSaveCustom() {
    await saveCustom(categories);
  }

  // Reset view to banner when drawer closes so it's
  // fresh if the user reopens preferences from the footer
  function handleOpenChange(open: boolean) {
    if (!open) setView('banner');
  }

  const Banner = () => {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-2">
        <DrawerHeader className="px-0">
          <div className="flex items-center gap-2 text-white">
            <Settings size={18} />
            <DrawerTitle className="[font-semibold text-base lg:text-[1.25rem]">
              Cookie Settings
            </DrawerTitle>
          </div>
          <DrawerDescription className="mt-1 text-left text-sm text-white">
            We use cookies to understand how visitors use our site. You can
            accept all, decline non-essential cookies, or{' '}
            <button
              onClick={() => setView('preferences')}
              className="text-zinc-300 underline underline-offset-2 transition-colors hover:text-white"
            >
              customize your preferences.
            </button>{' '}
            Read our{' '}
            <Link
              href="/privacy-policy"
              className="text-zinc-300 underline underline-offset-2 transition-colors hover:text-white"
            >
              Privacy Policy
            </Link>
            .
          </DrawerDescription>
        </DrawerHeader>

        <DrawerFooter className="flex-row justify-center gap-3 px-0 pb-6 lg:gap-6">
          <Button
            variant="outline"
            onClick={declineAll}
            className="border-zinc-200 bg-transparent text-white hover:bg-zinc-200 hover:text-black"
          >
            Decline All
          </Button>
          <Button
            onClick={acceptAll}
            className="bg-white text-zinc-900 hover:bg-zinc-300"
          >
            Accept All
          </Button>
        </DrawerFooter>
      </div>
    );
  };

  // ********************
  //     PREFERENCES
  // ********************
  const Preferences = () => (
    <div className="mx-auto w-full max-w-2xl px-6 py-2">
      <DrawerHeader className="px-0">
        <div className="flex items-center justify-between">
          <DrawerTitle className="text-base font-semibold text-white">
            Cookie Preferences
          </DrawerTitle>
          <button
            onClick={() => setView('banner')}
            className="text-sm text-zinc-200 underline underline-offset-4 transition-colors hover:text-white"
          >
            ← Back
          </button>
        </div>
        <DrawerDescription className="mt-2 text-sm text-white">
          Your choice will be saved for 12 months and can be updated at any time
          via the link in our footer.
        </DrawerDescription>
      </DrawerHeader>

      <div className="flex flex-col gap-3 py-2">
        {CATEGORIES.map((cat) => (
          <div
            key={cat.key}
            className="border-balck flex items-start justify-between gap-4 rounded-lg border bg-white p-4"
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor={`consent-${cat.key}`}
                  className="text-sm font-semibold text-black"
                >
                  {cat.label}
                </Label>
                {cat.required && (
                  <Badge
                    variant="secondary"
                    className="bg-rm-neutral-700 text-xs text-white"
                  >
                    Always on
                  </Badge>
                )}
              </div>
              <p className="text-xs leading-relaxed text-black">
                {cat.description}
              </p>
            </div>

            <Switch
              id={`consent-${cat.key}`}
              checked={categories[cat.key]}
              onCheckedChange={(val) => handleToggle(cat.key, val)}
              disabled={cat.required}
              className="mt-0.5 shrink-0 data-[state=checked]:bg-rm-green-400 data-[state=unchecked]:bg-black/60"
            />
          </div>
        ))}
      </div>

      <DrawerFooter className="flex-col justify-between gap-5 px-0 pb-6 md:flex-row">
        <div className="flex justify-between gap-6">
          <Button
            variant="outline"
            onClick={declineAll}
            className="w-1/2 bg-transparent text-white md:hover:text-black"
          >
            Decline All
          </Button>
          <Button
            onClick={acceptAll}
            className="w-1/2 border border-white bg-white text-black md:hover:bg-zinc-300"
          >
            Accept All
          </Button>
        </div>
        <Button
          onClick={handleSaveCustom}
          className="bg-zinc-200 text-zinc-900 hover:bg-zinc-200"
        >
          Save Preferences
        </Button>
      </DrawerFooter>
    </div>
  );

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange} dismissible={false}>
      <DrawerContent className="border-black bg-rm-neutral-700 text-white">
        {view === 'banner' ? <Banner /> : <Preferences />}
      </DrawerContent>
    </Drawer>
  );
}
